'use client';

/**
 * useRecoveryStats Hook
 *
 * Fetches recovery statistics from the API.
 * Shows total recoveries and token amounts with current USD values.
 */
import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import type { TokenPrice } from '@/lib/pricing-service/pricing-service';
export interface TokenTotal {
  chainId: number;
  address: string;
  symbol: string;
  decimals: number;
  totalAmount: string;
}
export interface RecoveryStatsResponse {
  totalRecoveries: number;
  tokenTotals: TokenTotal[];
  byChain?: Record<string, {
    totalRecoveries: number;
    tokenTotals: TokenTotal[];
  }>;
}
export interface UseRecoveryStatsOptions {
  chainId?: number;
  enabled?: boolean;
}
export interface UseRecoveryStatsReturn {
  stats: RecoveryStatsResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}
async function fetchRecoveryStats(chainId?: number): Promise<RecoveryStatsResponse> {
  const url = chainId ? `/api/masterchef/recovery/stats?chainId=${chainId}` : '/api/masterchef/recovery/stats';
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch recovery stats');
  }
  const data = await response.json();

  // When chainId is specified, API returns { chainId, stats }
  // Normalize to { totalRecoveries, tokenTotals } format
  if (chainId && data.stats) {
    return {
      totalRecoveries: data.stats.totalRecoveries,
      tokenTotals: data.stats.tokenTotals.map((t: TokenTotal) => ({
        ...t,
        chainId // Ensure chainId is set on each token
      }))
    };
  }
  return data;
}
export function useRecoveryStats(t0) {
  const $ = _c(14);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    chainId,
    enabled: t2
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  let t4;
  if ($[2] !== chainId) {
    t3 = ["recovery-stats", chainId];
    t4 = () => fetchRecoveryStats(chainId);
    $[2] = chainId;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== enabled || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      enabled,
      staleTime: 60000,
      refetchInterval: 300000
    };
    $[5] = enabled;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(t5);
  const t6 = data ?? null;
  const t7 = error as Error | null;
  let t8;
  if ($[9] !== isLoading || $[10] !== refetch || $[11] !== t6 || $[12] !== t7) {
    t8 = {
      stats: t6,
      isLoading,
      error: t7,
      refetch
    };
    $[9] = isLoading;
    $[10] = refetch;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}

/**
 * Calculate USD values for recovered tokens
 */
export function calculateRecoveryValue(tokenTotals: TokenTotal[], prices: Map<string, TokenPrice>): {
  totalValueUsd: number | null;
  tokenValues: Array<{
    address: string;
    symbol: string;
    amount: string;
    amountFormatted: string;
    valueUsd: number | null;
  }>;
} {
  let total = 0;
  let hasSomePrice = false;
  const tokenValues = tokenTotals.map(token => {
    const amount = BigInt(token.totalAmount);
    const amountFormatted = formatAmount(amount, token.decimals);
    const price = prices.get(token.address.toLowerCase());
    const valueUsd = price?.price_usd ? parseFloat(amountFormatted) * parseFloat(price.price_usd) : null;
    if (valueUsd !== null) {
      total += valueUsd;
      hasSomePrice = true;
    }
    return {
      address: token.address,
      symbol: token.symbol,
      amount: token.totalAmount,
      amountFormatted,
      valueUsd
    };
  });
  return {
    totalValueUsd: hasSomePrice ? total : null,
    tokenValues
  };
}
function formatAmount(amount: bigint, decimals: number): string {
  const divisor = BigInt(10 ** decimals);
  const whole = amount / divisor;
  const remainder = amount % divisor;
  if (remainder === 0n) {
    return whole.toString();
  }
  const remainderStr = remainder.toString().padStart(decimals, '0');
  // Trim trailing zeros
  const trimmed = remainderStr.replace(/0+$/, '');
  return `${whole}.${trimmed}`;
}